import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AlertTriangle, ArrowRight, RotateCcw, CheckCircle, Sparkles } from 'lucide-react';
import { SandboxPromoteModal } from './SandboxPromoteModal';

export const SandboxBanner: React.FC = () => {
  const { tenant, isSandbox, exitSandbox, resetSandbox } = useAuth();
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  if (!isSandbox && !tenant?.is_demo) {
    return null;
  }

  const handleReset = async () => {
    if (!window.confirm('Biztosan alaphelyzetbe állítod a Homokozót? Minden tesztelés során megadott adat törlődik, és újra az éles állapot másolata töltődik be.')) {
      return;
    }
    setIsResetting(true);
    const res = await resetSandbox();
    setIsResetting(false);
    if (res.ok) {
      setFeedback('A homokozó sikeresen alaphelyzetbe állt!');
      setTimeout(() => setFeedback(null), 4000);
    } else {
      alert(res.error || 'Hiba a reset során');
    }
  };

  return (
    <>
      <div className="bg-gradient-to-r from-amber-600 via-amber-500 to-orange-500 text-white px-4 py-2 text-xs shadow-md border-b border-amber-600/40 relative z-40">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-medium">
            <span className="p-1 rounded-md bg-amber-700/60 shadow-xs">
              <AlertTriangle className="w-4 h-4 text-amber-200 animate-pulse" />
            </span>
            <span className="font-bold uppercase tracking-wider bg-black/20 px-2 py-0.5 rounded text-[11px]">
              DEMO / Homokozó Mód
            </span>
            <span className="hidden sm:inline text-amber-100">
              Itt bátran tesztelhetsz, törölhetsz, hibázhatsz – az éles adatbázis 100%-ban védett!
            </span>
            {feedback && (
              <span className="inline-flex items-center gap-1 bg-emerald-700 text-white px-2 py-0.5 rounded text-[11px] font-bold animate-in fade-in">
                <CheckCircle className="w-3.5 h-3.5" />
                {feedback}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsPromoteModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-white text-amber-900 hover:bg-amber-50 font-bold shadow-xs hover:shadow transition cursor-pointer"
              title="A tesztelés során elkészített új vagy javított adatok átvétele az éles szervezetbe"
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-600" />
              <span>Tartalom átvétele az élesbe...</span>
            </button>

            <button
              onClick={handleReset}
              disabled={isResetting}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-700/80 hover:bg-amber-800 text-white font-semibold transition cursor-pointer disabled:opacity-50"
              title="Homokozó adatainak törlése és újraindítása az éles állapotból"
            >
              <RotateCcw className={`w-3.5 h-3.5 ${isResetting ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Homokozó Reset (Alaphelyzet)</span>
            </button>

            <button
              onClick={() => exitSandbox()}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-black/30 hover:bg-black/40 text-amber-100 hover:text-white font-semibold transition cursor-pointer"
              title="Kilépés a Homokozóból az éles szervezetbe"
            >
              <span>Kilépés</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {isPromoteModalOpen && (
        <SandboxPromoteModal
          isOpen={isPromoteModalOpen}
          onClose={() => setIsPromoteModalOpen(false)}
          onSuccess={(count) => {
            setIsPromoteModalOpen(false);
            setFeedback(`${count} db bejegyzés sikeresen átvéve az éles szervezetbe!`);
            setTimeout(() => setFeedback(null), 5000);
          }}
        />
      )}
    </>
  );
};
